import type { ComponentType } from "react";
import { useEffect } from "react";

import { useDispatch } from "react-redux";

import IntentApprovalContainer from "app/pages/sales-intelligence/sub-modules/intent/components/IntentApprovalContainer";
import useSignalExpirationHandler from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalExpirationHandler";
import useSignalsFetch from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalsFetch";
import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";
import {
    resetSignalsSourceAction,
    setSignalsSourceAction,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import type { UnknownProps } from "app/pages/sales-intelligence/types";

import SignalsManagementCommonContainer from "../containers/SignalsManagementCommonContainer";

type SignalSourceResolver = SignalSource | (() => SignalSource);

const resolveSignalSource = (source?: SignalSourceResolver): SignalSource =>
    typeof source === "function" ? source() : (source ?? SignalSource.Platform);

const withSignalsManagement = <P extends UnknownProps>(
    WrappedComponent: ComponentType<P>,
    signalsSource?: SignalSourceResolver,
): ComponentType<P> => {
    return function WrappedWithSignalsManagement(props: P) {
        const dispatch = useDispatch();
        const resolvedSource = resolveSignalSource(signalsSource);

        useEffect(() => {
            dispatch(setSignalsSourceAction(resolvedSource));

            return () => {
                dispatch(resetSignalsSourceAction());
            };
        }, [resolvedSource]);

        useSignalsFetch();
        useSignalExpirationHandler();

        return (
            <>
                <IntentApprovalContainer />
                <SignalsManagementCommonContainer />

                <WrappedComponent {...props} />
            </>
        );
    };
};

export default withSignalsManagement;
